import type { Municipio, Usuario } from '@/api/modelos'

/**
 * La sesion del panel: token, usuario y los municipios del usuario con sus
 * permisos en cada uno.
 *
 * Vive en memoria y se copia a sessionStorage para sobrevivir a un F5, nunca
 * a localStorage: el token dura 4 horas y no tiene por que quedar en el disco
 * del navegador despues de cerrar la pestaña.
 */
export type Sesion = {
  token: string
  usuario: Usuario
  municipios: Municipio[]
}

/** Por que se cerro la sesion, para que el login lo diga en pantalla. */
export type MotivoCierre = 'expiro' | 'salio' | 'clave_cambiada' | null

export type EstadoSesion = {
  sesion: Sesion | null
  motivoCierre: MotivoCierre
}

export const CLAVE_SESION = 'cobranza.sesion'

const oyentes = new Set<() => void>()

let estado: EstadoSesion = { sesion: leerDelNavegador(), motivoCierre: null }

function leerDelNavegador(): Sesion | null {
  try {
    const guardado = globalThis.sessionStorage?.getItem(CLAVE_SESION)
    if (guardado === null || guardado === undefined) {
      return null
    }

    const sesion = JSON.parse(guardado) as Sesion

    // Un JSON a medias vale lo mismo que no tener sesion.
    return typeof sesion?.token === 'string' && sesion.token !== '' ? sesion : null
  } catch {
    return null
  }
}

function escribirEnNavegador(sesion: Sesion | null): void {
  try {
    if (sesion === null) {
      globalThis.sessionStorage?.removeItem(CLAVE_SESION)
    } else {
      globalThis.sessionStorage?.setItem(CLAVE_SESION, JSON.stringify(sesion))
    }
  } catch {
    // Modo privado o almacenamiento lleno: la sesion sigue viva en memoria.
  }
}

function avisar(nuevo: EstadoSesion): void {
  estado = nuevo
  for (const oyente of oyentes) {
    oyente()
  }
}

export function obtenerEstado(): EstadoSesion {
  return estado
}

export function obtenerSesion(): Sesion | null {
  return estado.sesion
}

export function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente)

  return () => {
    oyentes.delete(oyente)
  }
}

export function guardarSesion(sesion: Sesion): void {
  escribirEnNavegador(sesion)
  avisar({ sesion, motivoCierre: null })
}

/** Refresca usuario y municipios sin tocar el token (respuesta de auth/yo). */
export function actualizarDatos(datos: Pick<Sesion, 'usuario' | 'municipios'>): void {
  if (estado.sesion === null) {
    return
  }

  const sesion: Sesion = { ...estado.sesion, ...datos }
  escribirEnNavegador(sesion)
  avisar({ sesion, motivoCierre: estado.motivoCierre })
}

/** Refresca solo la lista de municipios (respuesta de GET /municipios). */
export function actualizarMunicipios(municipios: Municipio[]): void {
  if (estado.sesion === null) {
    return
  }

  actualizarDatos({ usuario: estado.sesion.usuario, municipios })
}

export function limpiarSesion(motivo: MotivoCierre = null): void {
  escribirEnNavegador(null)
  avisar({ sesion: null, motivoCierre: motivo })
}

/** Solo para las pruebas: deja el almacen como recien cargado. */
export function reiniciarAlmacen(): void {
  escribirEnNavegador(null)
  estado = { sesion: null, motivoCierre: null }
  oyentes.clear()
}
